import { Button, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { Lock } from "@/api/locks";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaArrowRightToBracket, FaPencil, FaPlus } from "react-icons/fa6";
import { EditLockModal } from "@/app/components/modals/lock/editLockModal";
import { useLocation, useNavigate } from "react-router-dom";
import useErrorHandling from "@/hooks/useErrorHandling";
import { LocationChip } from "@/app/components/chips/locationChip";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { PLAN_FEATURES } from "@/app/store/plans/constants";
import { CreateCustomLockModal } from "@/app/components/modals/lock/custom/createCustomLockModal";
import { EditCustomLockModal } from "@/app/components/modals/lock/custom/editCustomLockModal";
import { CopyableElement } from "@/app/components/tables/renderers";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";

interface LockLocationInterface {
  id: number;
  display_name: string;
}

type LockLocation = LockLocationInterface | null;

export function LocksPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [rawLocks, setRawLocks] = useState<Lock[] | null>(null);
  const [locks, setLocks] = useState<Lock[] | null>(null);
  const [lock, setLock] = useState<Lock | null>(null);
  const [tabs, setTabs] = useState<LockLocation[] | null>(null);
  const [activeTab, setActiveTab] = useState<string | number>("all");

  const handleError = useErrorHandling();

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  const createLockModalDisclosure = useDisclosure();
  const editLockModalDisclosure = useDisclosure();
  const editCustomLockModalDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  const editCustomLock = (lock: Lock) => {
    setLock(lock);
    editCustomLockModalDisclosure.onOpen();
  };

  useEffect(() => {
    api.locks
      .list()
      .then((response) => {
        setRawLocks(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    createLockModalDisclosure.isOpen,
    editLockModalDisclosure.isOpen,
    editCustomLockModalDisclosure.isOpen,
    handleError,
  ]);

  useEffect(() => {
    const locations: LockLocation[] = [];

    rawLocks?.forEach((lock) => {
      if (lock.location) {
        if (
          !locations.find((loc) => loc?.id == lock.location?.id) &&
          lock.location?.id !== 0
        )
          locations.push(lock.location);
      }
    });

    setTabs(locations);
  }, [rawLocks]);

  useEffect(() => {
    if (activeTab === "all") return setLocks(rawLocks);
    const filteredLocks: Lock[] = [
      ...(rawLocks?.filter((lock) => lock.location?.id == activeTab) || []),
    ];
    return setLocks(filteredLocks);
  }, [rawLocks, activeTab]);

  return (
    <>
      <CreateCustomLockModal disclosure={createLockModalDisclosure} />
      <EditLockModal disclosure={editLockModalDisclosure} lock={lock} />
      <EditCustomLockModal
        disclosure={editCustomLockModalDisclosure}
        lockId={lock?.id || 0}
        defaults={
          lock
            ? {
                display_name: lock.display_name,
                lock_system: lock.lock_system,
                location: lock.location?.id,
                shared: lock.shared,
                meta: lock.meta,
              }
            : undefined
        }
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex flex-col gap-2 min-w-[240px] max-md:flex-grow">
          {/* @ts-ignore */}
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Замки</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                onPress={() => createLockModalDisclosure.onOpen()}>
                Создать замок
              </Button>
            </div>
          </div>
          <div className="flex flex-row gap-2 mb-4">
            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activeTab}
              onSelectionChange={setActiveTab}>
              <Tab key="all" title="Все локации" />
              {tabs?.map((tab) => (
                <Tab key={tab?.id} title={tab?.display_name}></Tab>
              ))}
            </Tabs>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              fullscreenDisclosure={fullscreenDisclosure}
              columns={[
                {
                  label: "Название замка",
                  key: "display_name",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  label: "Локация",
                  key: "location",
                  type: ColumnType.Custom,
                  render(_value, row) {
                    return <LocationChip locationId={row.location?.id} />;
                  },
                },
                {
                  label: "Система",
                  key: "lock_system",
                  type: ColumnType.LockSystem,
                  sortable: true,
                },
                {
                  label: "Сервисный ID",
                  key: "meta.id",
                  type: ColumnType.Custom,
                  sortable: true,
                  render(_, row) {
                    return <CopyableElement>{row.meta?.id}</CopyableElement>;
                  },
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaPencil />,
                      onClick: (_value, row) => {
                        editCustomLock(row);
                      },
                      planPermission: PLAN_FEATURES.LOCKS,
                    },
                    {
                      icon: <FaArrowRightToBracket />,
                      onClick: (_value, row) => {
                        dispatch(setSelectedLock(row.id));
                        navigate(`/dashboard/skud/logs`);
                      },
                      planPermission: PLAN_FEATURES.LOCKS,
                    },
                  ],
                },
              ]}
              data={locks || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "display_name",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
